/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */
import { GmcErrorCodes } from '@app/core/types';
import { SQLMetadataCompositeKey } from '@app/sql/types';
import { SQL_RESPONSE_PAGE_SIZE } from '@common/constants';
import { ClusterNode } from '@shared/types/node';
import {
  ClusterActionQueryResponseResult,
  ClusterActionScanExecuteArgument,
  ClusterActionSqlExecuteArgument,
  FrontendQueryParameters,
  FrontendQueryString,
  IgniteQueryString,
  Query,
  QueryType,
  ScanQuery,
  SqlExecuteQueryParameters,
  SqlQuery,
} from '@shared/types/queries';
import { Query3xExecutionResultOk } from '@shared/types/queries-3x';
import { pick } from 'lodash-es';

export const QUERIES_GROUP_NAME = 'queries';
const PUBLIC_SCHEMA = 'public';

export const convertToIgniteQuery = (
  query: SqlQuery,
): { parameters: SqlExecuteQueryParameters; igniteQuery: IgniteQueryString } => {
  const parameters: (string | undefined)[] = [];

  const resultQuery = replaceAndIgnoreSQLStrings(query.query, (queryText) => {
    let replace = true;
    let invalidParam = null;
    while (replace && !invalidParam) {
      replace = false;
      queryText = queryText.replace(namedParameterRegExp, (match, p1) => {
        replace = true;
        const param = query.parameters[p1];
        if (!param) invalidParam = p1;
        parameters.push(param);
        return '?';
      });
    }
    if (invalidParam)
      throw Object.assign(new Error(), { code: GmcErrorCodes.INVALID_QUERY_PARAMETER, parameter: invalidParam });
    return queryText;
  });

  return { parameters: parameters as SqlExecuteQueryParameters, igniteQuery: resultQuery as IgniteQueryString };
};

export const extractParametersFromQuery = (
  queryString: FrontendQueryString,
  currentParameters: FrontendQueryParameters,
): FrontendQueryParameters => {
  const newParameters = {} as FrontendQueryParameters;

  replaceAndIgnoreSQLStrings(queryString, (queryText) => {
    const matchers = queryText.match(/:[a-zA-Z0-9_\-]+(?=[ ;,)]|\n|$)/g);
    if (!matchers) return;
    const params = new Set(matchers.map((m) => m.substring(1)));
    params.forEach((p) => (newParameters[p] = currentParameters[p] || ''));
  });

  return newParameters;
};

export const replaceFrontendParametersWithValues = (
  query: FrontendQueryString,
  parameters: FrontendQueryParameters,
) => {
  Object.entries(parameters).forEach((pair) => {
    query = replaceAndIgnoreSQLStrings(query, (q) =>
      q.replace(new RegExp(`:${pair[0]}`, 'g'), () => (isNaN(+pair[1]) ? `'${pair[1]}'` : `${pair[1]}`)),
    ) as FrontendQueryString;
  });
  return query;
};

export const replaceActionParametersWithValues = (query: IgniteQueryString, parameters: SqlExecuteQueryParameters) => {
  parameters.forEach((p) => {
    query = replaceAndIgnoreSQLStrings(query, (q) => q.replace('?', isNaN(+p) ? `'${p}'` : p)) as IgniteQueryString;
  });
  return query;
};

export const getSqlMetadataCompositeKey = (clusterId: string, cacheName: string): SQLMetadataCompositeKey =>
  (clusterId + '/' + cacheName) as SQLMetadataCompositeKey;

export const getSqlMetadataCompositeKeyClusterId = (key: SQLMetadataCompositeKey): string | undefined =>
  key.split('/')[0];

export const extractStatementsFromQuery = (queryString: string) => {
  return replaceAndIgnoreSQLStrings(queryString, (query) => {
    query = query.replace(/(--[^\n]*(\n|$))|\/\*(.|\n)*?\*\//g, '');
    return query
      .split(';')
      .map((str) => str.trim())
      .filter((s) => s);
  });
};

export const convertQueryToExplainQuery = (queryString: string, is3x: boolean = false) => {
  if (!is3x) {
    return `EXPLAIN ${queryString}` as FrontendQueryString;
  } else {
    return `EXPLAIN PLAN FOR ${queryString}` as FrontendQueryString;
  }
};

const escapeSqlName = (name: string) => `"${name.replace(/"/g, '""')}"`;

export const selectAllColumnsFromTableQuery = (schemaName: string, tableName: string, isCluster3x: boolean) => {
  if (isCluster3x && schemaName.toLowerCase() !== PUBLIC_SCHEMA) {
    // For 3x clusters with non-public schema, include schema name
    return `SELECT * FROM ${escapeSqlName(schemaName)}.${escapeSqlName(tableName)} LIMIT 100` as FrontendQueryString;
  } else {
    // For 2x clusters or 3x clusters with public schema, use the original format
    return `SELECT * FROM ${escapeSqlName(tableName)} LIMIT 100` as FrontendQueryString;
  }
};

export const selectColumnFromTableQuery = (tableName: string, columnName: string) => {
  return `SELECT ${escapeSqlName(columnName)} FROM ${escapeSqlName(tableName)} LIMIT 100` as FrontendQueryString;
};

export const isExplain = (result: ClusterActionQueryResponseResult | Query3xExecutionResultOk) =>
  result.columns.length === 1 && !result.columns[0]?.schemaName && result.columns[0]?.fieldName === 'PLAN';

const namedParameterRegExp = /:([^ ,;)\n]*)/g;

/* This function removes all strings from query, after that executes given function (second argument)
  and restores all hidden strings.
  It is done to avoid cases when some operations (like replacing named parameters with '?') affects on strings in query.
  Please use this function in case if you want to change a query somehow, or extract something from it.
 */
const replaceAndIgnoreSQLStrings = <T extends string | string[] | void>(
  query: string,
  callback: (str: string) => T,
): T extends string ? string : T extends string[] ? string[] : void => {
  const matcher = /'.*?[^']'(?!')/g;
  const matchedStrings: string[] = query.match(matcher) ?? [];

  if (matchedStrings.length) {
    query = query.replace(matcher, '*_*');
  }

  const result: any = callback(query);

  if (!result) {
    return undefined as any;
  } else {
    const replace = (str: string): any => str.replace(/\*_\*/g, () => matchedStrings.shift()!);

    return Array.isArray(result) ? result.map((q) => replace(q)) : replace(result);
  }
};

export type DispatchSqlArgs<T extends ClusterActionSqlExecuteArgument | ClusterActionScanExecuteArgument> = {
  clusterId: string;
  argument: T;
  nodeIds?: string[];
};

export const prepareExecuteSqlArgs = (query: SqlQuery, clusterId: string, nodes: ClusterNode[]) => {
  const { parameters, igniteQuery } = convertToIgniteQuery(query);
  const settings = pick(query, 'distributedJoins', 'lazy', 'enforceJoinOrder', 'defaultSchema');
  const args = {
    clusterId,
    argument: {
      queryId: query.id,
      pageSize: SQL_RESPONSE_PAGE_SIZE,
      queryText: igniteQuery,
      parameters,
      ...settings,
    },
  } as DispatchSqlArgs<ClusterActionSqlExecuteArgument>;
  if (query.selectedNodeConsistentId) {
    const node = nodes.find((node) => node.consistentId === query.selectedNodeConsistentId);
    if (!node || !node.nodeId) {
      throw `Node with consistent Id '${query.selectedNodeConsistentId}' is not online`;
    }
    args.nodeIds = [node.nodeId];
    args.argument.local = true;
  }

  return args;
};

export const prepareExecuteScanQueryArgs = (query: ScanQuery, clusterId: string, nodes: ClusterNode[]) => {
  const args = {
    clusterId,
    argument: {
      queryId: query.id,
      pageSize: SQL_RESPONSE_PAGE_SIZE,
      cacheName: query.cacheName,
      partition: query.partition,
      keyFilterRegex: query.keyFilterRegex,
      valueFilterRegex: query.valueFilterRegex,
    },
  } as DispatchSqlArgs<ClusterActionScanExecuteArgument>;
  if (query.nodeConsistentId) {
    const node = nodes.find((node) => node.consistentId === query.nodeConsistentId);
    if (!node || !node.nodeId) {
      throw `Node with consistent Id ${query.nodeConsistentId} is not online`;
    }
    args.nodeIds = [node.nodeId];
    args.argument.local = true;
  }
  return args;
};

export const isSqlQuery = (query: Query | undefined): query is SqlQuery => {
  return query?.type === QueryType.SQL && query?.query !== undefined;
};
